import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AxiosError } from 'axios'
import {
  getPaymentRelinkCandidates,
  getQuickBooksInvoicePayments,
  getQuickBooksInvoices,
  getRelinkCandidates,
  reapplyQuickBooksPayment,
  ReapplyQuickBooksPaymentRequest,
  ReapplyQuickBooksPaymentResult,
  relinkQuickBooksInvoice,
  relinkQuickBooksPayment,
  RelinkQuickBooksPaymentRequest,
  RelinkQuickBooksPaymentResult,
  RelinkQuickBooksInvoiceRequest,
  RelinkQuickBooksInvoiceResult,
  resyncQuickBooksInvoices,
  ResyncQuickBooksInvoicesRequest,
  ResyncQuickBooksInvoicesResult,
} from './quickBooksResyncApi'

const invoicesQueryKey = (tenantId: number) => ['admin', 'quickbooks-invoices', tenantId]

const invoicePaymentsQueryKey = (tenantId: number, invoiceId: string) =>
  ['admin', 'quickbooks-invoice-payments', tenantId, invoiceId]

export const useQuickBooksInvoicesQuery = (tenantId: number | undefined) => {
  return useQuery(invoicesQueryKey(tenantId ?? 0), {
    queryFn: () => getQuickBooksInvoices(tenantId as number),
    enabled: tenantId !== undefined,
    refetchOnWindowFocus: false,
  })
}

export const useResyncQuickBooksInvoicesMutation = () => {
  return useMutation<ResyncQuickBooksInvoicesResult, AxiosError, ResyncQuickBooksInvoicesRequest>({
    mutationFn: (request: ResyncQuickBooksInvoicesRequest) => resyncQuickBooksInvoices(request),
  })
}

export const useRelinkCandidatesQuery = (tenantId: number | undefined, invoiceId: string | undefined) => {
  return useQuery(['admin', 'quickbooks-relink-candidates', tenantId ?? 0, invoiceId ?? ''], {
    queryFn: () => getRelinkCandidates(tenantId as number, invoiceId as string),
    enabled: tenantId !== undefined && invoiceId !== undefined,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  })
}

export const useRelinkQuickBooksInvoiceMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<RelinkQuickBooksInvoiceResult, AxiosError, RelinkQuickBooksInvoiceRequest>({
    mutationFn: (request: RelinkQuickBooksInvoiceRequest) => relinkQuickBooksInvoice(request),
    onSuccess: (_result, request) => {
      queryClient.invalidateQueries(invoicesQueryKey(request.tenantId))
    },
  })
}

export const useQuickBooksInvoicePaymentsQuery = (tenantId: number | undefined, invoiceId: string | undefined) => {
  return useQuery(invoicePaymentsQueryKey(tenantId ?? 0, invoiceId ?? ''), {
    queryFn: () => getQuickBooksInvoicePayments(tenantId as number, invoiceId as string),
    enabled: tenantId !== undefined && invoiceId !== undefined,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  })
}

export const useReapplyQuickBooksPaymentMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<ReapplyQuickBooksPaymentResult, AxiosError, ReapplyQuickBooksPaymentRequest>({
    mutationFn: (request: ReapplyQuickBooksPaymentRequest) => reapplyQuickBooksPayment(request),
    onSuccess: (_result, request) => {
      queryClient.invalidateQueries(invoicePaymentsQueryKey(request.tenantId, request.invoiceId))
      queryClient.invalidateQueries(invoicesQueryKey(request.tenantId))
    },
    onError: (_error, request) => {
      queryClient.invalidateQueries(invoicePaymentsQueryKey(request.tenantId, request.invoiceId))
    },
  })
}

export const usePaymentRelinkCandidatesQuery = (
  tenantId: number | undefined,
  invoiceId: string | undefined,
  paymentId: string | undefined
) => {
  return useQuery(['admin', 'quickbooks-payment-relink-candidates', tenantId ?? 0, invoiceId ?? '', paymentId ?? ''], {
    queryFn: () => getPaymentRelinkCandidates(tenantId as number, invoiceId as string, paymentId as string),
    enabled: tenantId !== undefined && invoiceId !== undefined && paymentId !== undefined,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  })
}

export const useRelinkQuickBooksPaymentMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<RelinkQuickBooksPaymentResult, AxiosError, RelinkQuickBooksPaymentRequest>({
    mutationFn: (request: RelinkQuickBooksPaymentRequest) => relinkQuickBooksPayment(request),
    onSuccess: (_result, request) => {
      queryClient.invalidateQueries(invoicePaymentsQueryKey(request.tenantId, request.invoiceId))
      queryClient.invalidateQueries(invoicesQueryKey(request.tenantId))
    },
  })
}
